import { Injectable } from "@angular/core";

import { State } from "../classes/domain/entities/website/masters/state/state";
import { Zone } from "../classes/domain/entities/website/masters/zone/zone";
import { District } from "../classes/domain/entities/website/masters/district/district";
import { Block } from "../classes/domain/entities/website/masters/block/block";
import { BcmPostMaster } from "../classes/domain/entities/website/masters/sanctioned-posts/bcm-post/bcmpost";
import { PHC } from "../classes/domain/entities/website/masters/phc/phc";
import { AFPostMaster } from "../classes/domain/entities/website/masters/sanctioned-posts/af-post/afpost";
import { Village } from "../classes/domain/entities/website/masters/village/village";
import { AshaEmployeeRecord } from "../classes/domain/entities/website/masters/asha/asha-employee-record/ashaemployeerecord";
import { SC } from "../classes/domain/entities/website/masters/sc/sc";

import { BehaviorSubject } from "rxjs";

import {
  DashboardFilterStateStorageService
} from "./dashboard-filter-state-storage.service";

import { UIUtils } from "./uiutils.service";
import { Roles } from "../classes/infrastructure/enums";


interface DashboardFilterSnapshot {

  state_ids: number[];
  zone_ids: number[];
  district_ids: number[];
  block_ids: number[];
  bcm_post_ids: number[];
  phc_ids: number[];
  af_ids: number[];
  sc_ids: number[];
  village_ids: number[];
  asha_ids: number[];

  StateList: State[];
  ZoneList: Zone[];
  DistrictList: District[];
  BlockList: Block[];
  BCMPostList: BcmPostMaster[];
  PHCList: PHC[];
  AFList: AFPostMaster[];
  SCList: SC[];
  VillageList: Village[];
  AshaList: AshaEmployeeRecord[];

  IsStateDisabled: boolean;
  IsZoneDisabled: boolean;
  IsDistrictDisabled: boolean;
  IsBlockDisabled: boolean;
  IsBCMPostDisabled: boolean;
  IsPHCDisabled: boolean;
  IsAFPostDisabled: boolean;
  IsSCDisabled: boolean;
  IsVillageDisabled: boolean;
  IsAshaDisabled: boolean;
}


@Injectable({
  providedIn: "root"
})
export class DashboardFilterService {

  // ============================================================
  // SELECTED IDS
  // ============================================================

  state_ids: number[] = [];
  zone_ids: number[] = [];
  district_ids: number[] = [];
  block_ids: number[] = [];
  bcm_post_ids: number[] = [];
  phc_ids: number[] = [];
  af_ids: number[] = [];
  sc_ids: number[] = [];
  village_ids: number[] = [];
  asha_ids: number[] = [];


  // ============================================================
  // LISTS
  // ============================================================

  StateList: State[] = [];
  ZoneList: Zone[] = [];
  DistrictList: District[] = [];
  BlockList: Block[] = [];
  BCMPostList: BcmPostMaster[] = [];
  PHCList: PHC[] = [];
  AFList: AFPostMaster[] = [];
  SCList: SC[] = [];
  VillageList: Village[] = [];
  AshaList: AshaEmployeeRecord[] = [];


  // ============================================================
  // DISABLED FLAGS
  // ============================================================

  IsStateDisabled = false;
  IsZoneDisabled = false;
  IsDistrictDisabled = false;
  IsBlockDisabled = false;
  IsBCMPostDisabled = false;
  IsPHCDisabled = false;
  IsAFPostDisabled = false;
  IsSCDisabled = false;
  IsVillageDisabled = false;
  IsAshaDisabled = false;


  // ============================================================
  // VISIBILITY
  // ============================================================

  IsStateVisible = true;
  IsZoneVisible = true;
  IsDistrictVisible = true;
  IsBlockVisible = true;
  IsBCMPostVisible = true;
  IsPHCVisible = true;
  IsAFPostVisible = true;
  IsSCVisible = true;
  IsVillageVisible = true;
  IsAshaVisible = true;


  IsFilterLoading = false;

  isFilterOpen = false;

  /**
   * Controls filter persistence:
   * - 'dashboard_filter_state' (default): Syncs with dashboard session storage.
   * - custom string (e.g. 'abortion_report_filter'): Persists only for that report in sessionStorage.
   * - null: Disables persistence entirely (fresh in-memory state on every visit).
   */
  storageKey: string | null = 'dashboard_filter_state';

  get persistToStorage(): boolean {
    return this.storageKey !== null;
  }

  set persistToStorage(val: boolean) {
    if (!val) {
      this.storageKey = null;
    } else if (!this.storageKey) {
      this.storageKey = 'dashboard_filter_state';
    }
  }


  // ============================================================
  // IMPORTANT FLAGS
  // ============================================================

  /**
   * TRUE while the complete hierarchy is being restored/loaded.
   *
   * DevExtreme can fire onValueChanged while its value is being
   * assigned programmatically. We MUST ignore those events.
   */
  private isLoadingHierarchy = false;


  /**
   * TRUE specifically while saved session state is being restored.
   */
  private isRestoringFilter = false;


  // ============================================================
  // SNAPSHOT
  // ============================================================

  private appliedSnapshot:
    DashboardFilterSnapshot | null = null;


  // ============================================================
  // OBSERVABLE
  // ============================================================

  filterState$ =
    new BehaviorSubject<any>({});


  constructor(
    private uiUtils: UIUtils,
    private filterStorage:
      DashboardFilterStateStorageService
  ) {}


  // ============================================================
  // INITIALIZATION CHECK
  // ============================================================

  get isInitializing(): boolean {

    return (
      this.isLoadingHierarchy ||
      this.isRestoringFilter
    );
  }


  // ============================================================
  // OPEN FILTER
  // ============================================================

  openFilter(): void {

    /*
     * IMPORTANT:
     *
     * Do NOT call LoadFilterDataUPTOStateLevel()
     * here.
     *
     * The hierarchy has already been loaded.
     * Re-loading it here causes DevExtreme change
     * events and reset methods to execute again.
     */

    this.captureSnapshot();

    this.isFilterOpen = true;
  }


  // ============================================================
  // CLOSE FILTER
  // ============================================================

  closeFilter(): void {

    this.isFilterOpen = false;
  }


  // ============================================================
  // SNAPSHOT
  // ============================================================

  private captureSnapshot(): void {

    this.appliedSnapshot = {

      state_ids: [...this.state_ids],
      zone_ids: [...this.zone_ids],
      district_ids: [...this.district_ids],
      block_ids: [...this.block_ids],
      bcm_post_ids: [...this.bcm_post_ids],
      phc_ids: [...this.phc_ids],
      af_ids: [...this.af_ids],
      sc_ids: [...this.sc_ids],
      village_ids: [...this.village_ids],
      asha_ids: [...this.asha_ids],

      StateList: [...this.StateList],
      ZoneList: [...this.ZoneList],
      DistrictList: [...this.DistrictList],
      BlockList: [...this.BlockList],
      BCMPostList: [...this.BCMPostList],
      PHCList: [...this.PHCList],
      AFList: [...this.AFList],
      SCList: [...this.SCList],
      VillageList: [...this.VillageList],
      AshaList: [...this.AshaList],

      IsStateDisabled: this.IsStateDisabled,
      IsZoneDisabled: this.IsZoneDisabled,
      IsDistrictDisabled: this.IsDistrictDisabled,
      IsBlockDisabled: this.IsBlockDisabled,
      IsBCMPostDisabled: this.IsBCMPostDisabled,
      IsPHCDisabled: this.IsPHCDisabled,
      IsAFPostDisabled: this.IsAFPostDisabled,
      IsSCDisabled: this.IsSCDisabled,
      IsVillageDisabled: this.IsVillageDisabled,
      IsAshaDisabled: this.IsAshaDisabled
    };
  }


  // ============================================================
  // DISCARD
  // ============================================================

  discardUnappliedChanges(): void {

    const s = this.appliedSnapshot;

    if (!s) {
      return;
    }

    this.state_ids = [...s.state_ids];
    this.zone_ids = [...s.zone_ids];
    this.district_ids = [...s.district_ids];
    this.block_ids = [...s.block_ids];
    this.bcm_post_ids = [...s.bcm_post_ids];
    this.phc_ids = [...s.phc_ids];
    this.af_ids = [...s.af_ids];
    this.sc_ids = [...s.sc_ids];
    this.village_ids = [...s.village_ids];
    this.asha_ids = [...s.asha_ids];

    this.StateList = [...s.StateList];
    this.ZoneList = [...s.ZoneList];
    this.DistrictList = [...s.DistrictList];
    this.BlockList = [...s.BlockList];
    this.BCMPostList = [...s.BCMPostList];
    this.PHCList = [...s.PHCList];
    this.AFList = [...s.AFList];
    this.SCList = [...s.SCList];
    this.VillageList = [...s.VillageList];
    this.AshaList = [...s.AshaList];

    this.IsStateDisabled = s.IsStateDisabled;
    this.IsZoneDisabled = s.IsZoneDisabled;
    this.IsDistrictDisabled = s.IsDistrictDisabled;
    this.IsBlockDisabled = s.IsBlockDisabled;
    this.IsBCMPostDisabled = s.IsBCMPostDisabled;
    this.IsPHCDisabled = s.IsPHCDisabled;
    this.IsAFPostDisabled = s.IsAFPostDisabled;
    this.IsSCDisabled = s.IsSCDisabled;
    this.IsVillageDisabled = s.IsVillageDisabled;
    this.IsAshaDisabled = s.IsAshaDisabled;
  }


  // ============================================================
  // LOADING
  // ============================================================

  private async withLoading(
    fn: () => Promise<void>
  ): Promise<void> {

    this.IsFilterLoading = true;

    try {

      await fn();

    } finally {

      this.IsFilterLoading = false;
    }
  }


  // ============================================================
  // ROLE
  // ============================================================

  private getCurrentRole(): Roles {

    const role =
      sessionStorage.getItem("Role");

    return role
      ? (Number(role) as Roles)
      : Roles.None;
  }


  // ============================================================
  // ROLE VISIBILITY
  // ============================================================

  public applyRoleVisibility(): void {

    const role =
      this.getCurrentRole();

    this.IsStateVisible = true;
    this.IsZoneVisible = true;
    this.IsDistrictVisible = true;
    this.IsBlockVisible = true;
    this.IsBCMPostVisible = true;
    this.IsPHCVisible = true;
    this.IsAFPostVisible = true;
    this.IsSCVisible = true;
    this.IsVillageVisible = true;
    this.IsAshaVisible = true;


    switch (role) {

      case Roles.DHO:
      case Roles.DCM:

        this.IsStateVisible = false;
        this.IsZoneVisible = false;

        break;


      case Roles.THO:
      case Roles.BCM:

        this.IsStateVisible = false;
        this.IsZoneVisible = false;
        this.IsDistrictVisible = false;

        break;


      case Roles.AF:
      case Roles.MO:

        this.IsStateVisible = false;
        this.IsZoneVisible = false;
        this.IsDistrictVisible = false;
        this.IsBlockVisible = false;
        this.IsBCMPostVisible = false;

        break;


      case Roles.Asha:

        this.IsStateVisible = false;
        this.IsZoneVisible = false;
        this.IsDistrictVisible = false;
        this.IsBlockVisible = false;
        this.IsBCMPostVisible = false;
        this.IsPHCVisible = false;
        this.IsAFPostVisible = false;
        this.IsSCVisible = false;
        this.IsVillageVisible = false;
        this.IsAshaVisible = false;

        break;


      default:
        break;
    }
  }


  // ============================================================
  // AUTO SELECT
  // ============================================================

  private async autoSelectIfSingle<T extends {
    p: { id: number }
  }>(
    list: T[],
    getIds: () => number[],
    setIds: (ids: number[]) => void,
    setDisabled: (value: boolean) => void,
    next?: () => Promise<void>
  ): Promise<void> {


    /*
     * IMPORTANT:
     *
     * If a saved selection already exists,
     * NEVER overwrite it.
     */

    const currentIds =
      getIds();


    if (currentIds.length > 0) {

      setDisabled(
        list.length === 1
      );

      if (next) {
        await next();
      }

      return;
    }


    if (list.length === 1) {

      setIds([
        list[0].p.id
      ]);

      setDisabled(true);

      if (next) {
        await next();
      }

      return;
    }


    setDisabled(false);
  }


  // ============================================================
  // MAIN LOAD
  // ============================================================

  async LoadFilterDataUPTOStateLevel(): Promise<void> {

    /*
     * VERY IMPORTANT:
     *
     * Keep this TRUE for the ENTIRE hierarchy loading.
     *
     * Previously isInitializing was only incremented
     * inside autoSelectIfSingle(), which allowed DevExtreme
     * onValueChanged handlers to execute during loading.
     */

    if (this.isLoadingHierarchy) {
      return;
    }


    this.isLoadingHierarchy = true;
    this.isRestoringFilter = true;


    try {

      await this.withLoading(async () => {

        // ======================================================
        // RESTORE SESSION STATE
        // ======================================================

        this.restoreSavedFilterState();


        // ======================================================
        // ROLE
        // ======================================================

        const role =
          this.getCurrentRole();


        // ======================================================
        // VISIBILITY
        // ======================================================

        this.applyRoleVisibility();


        // ======================================================
        // LOAD HIERARCHY
        // ======================================================

        if (
          role === Roles.SuperAdmin ||
          role === Roles.Developer
        ) {

          await this.loadFromStateLevel();

        }

        else if (
          role === Roles.DHO ||
          role === Roles.DCM
        ) {

          await this.loadFromDistrictLevel();

        }

        else if (
          role === Roles.THO ||
          role === Roles.BCM
        ) {

          await this.loadFromBlockLevel();

        }

        else if (
          role === Roles.AF ||
          role === Roles.MO
        ) {

          await this.loadFromPHCLevel();

        }

      });


      // ========================================================
      // COMMIT RESTORED STATE AS SNAPSHOT
      // ========================================================

      this.captureSnapshot();


      console.log(
        "FINAL DASHBOARD FILTER STATE:",
        {
          state_ids: this.state_ids,
          zone_ids: this.zone_ids,
          district_ids: this.district_ids,
          block_ids: this.block_ids,
          bcm_post_ids: this.bcm_post_ids,
          phc_ids: this.phc_ids,
          af_ids: this.af_ids,
          sc_ids: this.sc_ids,
          village_ids: this.village_ids,
          asha_ids: this.asha_ids
        }
      );

    }

    finally {

      /*
       * Do NOT set these flags to false before
       * the complete hierarchy is loaded.
       */

      this.isRestoringFilter = false;
      this.isLoadingHierarchy = false;
    }
  }


  // ============================================================
  // STATE LEVEL
  // ============================================================

  private async loadFromStateLevel(): Promise<void> {

    try {

      this.StateList =
        await State.FetchEntireList();


      // Keep saved IDs that still exist.
      this.state_ids =
        this.state_ids.filter(id =>
          this.StateList.some(
            state => state.p.id === id
          )
        );


      if (this.state_ids.length === 0) {

        await this.autoSelectIfSingle(
          this.StateList,
          () => this.state_ids,
          ids => this.state_ids = ids,
          value => this.IsStateDisabled = value,
          async () => await this.fetchZones()
        );

      } else {

        this.IsStateDisabled = false;

        await this.fetchZones();
      }

    } catch (err: any) {

      this.uiUtils.showErrorMessage(
        "Error",
        err?.message || String(err)
      );
    }
  }


  // ============================================================
  // ZONE
  // ============================================================

  private async fetchZones(): Promise<void> {

    try {

      if (!this.state_ids.length) {

        this.ZoneList = [];

        /*
         * During restore, don't destroy lower-level
         * saved selections.
         */
        if (!this.isRestoringFilter) {
          this.resetFromZoneDown();
        }

        return;
      }


      this.ZoneList =
        await Zone.FetchEntireListByStateRefs(
          this.state_ids
        );


      this.zone_ids =
        this.zone_ids.filter(id =>
          this.ZoneList.some(
            zone => zone.p.id === id
          )
        );


      await this.autoSelectIfSingle(
        this.ZoneList,
        () => this.zone_ids,
        ids => this.zone_ids = ids,
        value => this.IsZoneDisabled = value,
        async () => await this.fetchDistricts()
      );


      // If saved/multiple zone selection exists,
      // continue manually.
      if (
        this.zone_ids.length > 0 &&
        this.DistrictList.length === 0
      ) {

        await this.fetchDistricts();
      }

    } catch (err: any) {

      this.uiUtils.showErrorMessage(
        "Error",
        err?.message || String(err)
      );
    }
  }


  // ============================================================
  // DISTRICT
  // ============================================================

  private async fetchDistricts(): Promise<void> {

    try {

      if (!this.zone_ids.length) {

        this.DistrictList = [];

        if (!this.isRestoringFilter) {
          this.resetFromDistrictDown();
        }

        return;
      }


      this.DistrictList =
        await District.FetchEntireListByWithZoneRefs(
          this.zone_ids
        );


      this.district_ids =
        this.district_ids.filter(id =>
          this.DistrictList.some(
            district => district.p.id === id
          )
        );


      await this.autoSelectIfSingle(
        this.DistrictList,
        () => this.district_ids,
        ids => this.district_ids = ids,
        value => this.IsDistrictDisabled = value,
        async () => await this.fetchBlocks()
      );


      if (
        this.district_ids.length > 0 &&
        this.BlockList.length === 0
      ) {

        await this.fetchBlocks();
      }

    } catch (err: any) {

      this.uiUtils.showErrorMessage(
        "Error",
        err?.message || String(err)
      );
    }
  }


  // ============================================================
  // DISTRICT ROLE
  // ============================================================

  private async loadFromDistrictLevel(): Promise<void> {

    try {

      const districtList =
        await District.FetchEntireList();


      if (districtList.length === 0) {

        this.uiUtils.showErrorMessage(
          "Error",
          "No district assigned to your account."
        );

        return;
      }


      if (districtList.length > 1) {

        this.uiUtils.showErrorMessage(
          "Error",
          "Multiple districts found. Please contact administrator."
        );

        return;
      }


      this.DistrictList =
        districtList;


      this.district_ids =
        [districtList[0].p.id];


      this.IsDistrictDisabled = true;


      await this.fetchBlocks();

    } catch (err: any) {

      this.uiUtils.showErrorMessage(
        "Error",
        err?.message || String(err)
      );
    }
  }


  // ============================================================
  // BLOCK
  // ============================================================

  private async fetchBlocks(): Promise<void> {

    try {

      if (!this.district_ids.length) {

        this.BlockList = [];

        if (!this.isRestoringFilter) {
          this.resetFromBlockDown();
        }

        return;
      }


      this.BlockList =
        await Block.FetchEntireListByDistrictRefs(
          this.district_ids
        );


      /*
       * IMPORTANT:
       *
       * This preserves the session value:
       *
       * block_ids = [1000000000001616]
       *
       * if that block exists in the returned list.
       */

      this.block_ids =
        this.block_ids.filter(id =>
          this.BlockList.some(
            block => block.p.id === id
          )
        );


      await this.autoSelectIfSingle(
        this.BlockList,
        () => this.block_ids,
        ids => this.block_ids = ids,
        value => this.IsBlockDisabled = value,
        async () => await this.fetchBCMPosts()
      );


      if (
        this.block_ids.length > 0 &&
        this.BCMPostList.length === 0
      ) {

        await this.fetchBCMPosts();
      }


      console.log(
        "BLOCK IDS AFTER FETCH:",
        this.block_ids
      );

    } catch (err: any) {

      this.uiUtils.showErrorMessage(
        "Error",
        err?.message || String(err)
      );
    }
  }


  // ============================================================
  // BLOCK ROLE
  // ============================================================

  private async loadFromBlockLevel(): Promise<void> {

    try {

      const blockList =
        await Block.FetchEntireList();


      if (blockList.length === 0) {

        this.uiUtils.showErrorMessage(
          "Error",
          "No block assigned to your account."
        );

        return;
      }


      if (blockList.length > 1) {

        this.uiUtils.showErrorMessage(
          "Error",
          "Multiple blocks found. Please contact administrator."
        );

        return;
      }


      this.BlockList =
        blockList;


      this.block_ids =
        [blockList[0].p.id];


      this.IsBlockDisabled = true;


      await this.fetchBCMPosts();

    } catch (err: any) {

      this.uiUtils.showErrorMessage(
        "Error",
        err?.message || String(err)
      );
    }
  }


  // ============================================================
  // BCM POSTS
  // ============================================================

  private async fetchBCMPosts(): Promise<void> {

    try {

      if (!this.block_ids.length) {

        this.BCMPostList = [];

        if (!this.isRestoringFilter) {
          this.resetFromBCMPostDown();
        }

        return;
      }


      this.BCMPostList =
        await BcmPostMaster.FetchEntireListByBlockRefs(
          this.block_ids
        );


      this.bcm_post_ids =
        this.bcm_post_ids.filter(id =>
          this.BCMPostList.some(
            bcm => bcm.p.id === id
          )
        );


      await this.autoSelectIfSingle(
        this.BCMPostList,
        () => this.bcm_post_ids,
        ids => this.bcm_post_ids = ids,
        value => this.IsBCMPostDisabled = value,
        async () => await this.fetchPHCs()
      );


      if (
        this.bcm_post_ids.length > 0 &&
        this.PHCList.length === 0
      ) {

        await this.fetchPHCs();
      }

    } catch (err: any) {

      this.uiUtils.showErrorMessage(
        "Error",
        err?.message || String(err)
      );
    }
  }


  // ============================================================
  // PHC
  // ============================================================

  private async fetchPHCs(): Promise<void> {

    try {

      if (!this.bcm_post_ids.length) {

        this.PHCList = [];

        if (!this.isRestoringFilter) {
          this.resetFromPHCDown();
        }

        return;
      }


      this.PHCList =
        await PHC.FetchEntireListByBCMPostRefs(
          this.bcm_post_ids
        );


      this.phc_ids =
        this.phc_ids.filter(id =>
          this.PHCList.some(
            phc => phc.p.id === id
          )
        );


      await this.autoSelectIfSingle(
        this.PHCList,
        () => this.phc_ids,
        ids => this.phc_ids = ids,
        value => this.IsPHCDisabled = value,
        async () => await this.fetchAFs()
      );

      if (
        this.phc_ids.length > 0 &&
        this.AFList.length === 0
      ) {

        await this.fetchAFs();
      }

    } catch (err: any) {

      this.uiUtils.showErrorMessage(
        "Error",
        err?.message || String(err)
      );
    }
  }


  // ============================================================
  // PHC ROLE
  // ============================================================

  private async loadFromPHCLevel(): Promise<void> {

    try {
      const phcList =
        await PHC.FetchEntireList();

      if (phcList.length === 0) {

        this.uiUtils.showErrorMessage(
          "Error",
          "No PHC assigned to your account."
        );

        return;
      }

      if (phcList.length > 1) {

        this.uiUtils.showErrorMessage(
          "Error",
          "Multiple PHCs found. Please contact administrator."
        );
        return;
      }

      this.PHCList =
        phcList;

      this.phc_ids =
        [phcList[0].p.id];

      this.IsPHCDisabled = true;
      await this.fetchAFs();

    } catch (err: any) {
      this.uiUtils.showErrorMessage(
        "Error",
        err?.message || String(err)
      );
    }
  }


  // ============================================================
  // AF
  // ============================================================

  private async fetchAFs(): Promise<void> {
    try {
      if (!this.phc_ids.length) {
        this.AFList = [];
        if (!this.isRestoringFilter) {
          this.af_ids = [];
          this.resetFromSCDown();
        }
        return;
      }

      this.AFList =
        await AFPostMaster.FetchEntireListByPHCRefs(
          this.phc_ids
        );

      const role =
        this.getCurrentRole();

      if (
        role === Roles.AF ||
        role === Roles.MO
      ) {

        if (this.AFList.length === 0) {

          this.uiUtils.showErrorMessage(
            "Error",
            "No AF assigned to your account."
          );
          return;
        }

        if (this.AFList.length > 1) {

          this.uiUtils.showErrorMessage(
            "Error",
            "Multiple AFs found. Please contact administrator."
          );
          return;
        }
      }

      this.af_ids =
        this.af_ids.filter(id =>
          this.AFList.some(
            af => af.p.id === id
          )
        );

      await this.autoSelectIfSingle(
        this.AFList,
        () => this.af_ids,
        ids => this.af_ids = ids,
        value => this.IsAFPostDisabled = value,
        async () => await this.fetchSCs()
      );

      if (
        this.af_ids.length > 0 &&
        this.SCList.length === 0
      ) {
        await this.fetchSCs();
      }

    } catch (err: any) {
      this.uiUtils.showErrorMessage(
        "Error",
        err?.message || String(err)
      );
    }
  }


  // ============================================================
  // SC
  // ============================================================

  private async fetchSCs(): Promise<void> {

    try {

      if (!this.af_ids.length) {
        this.SCList = [];
        if (!this.isRestoringFilter) {
          this.sc_ids = [];
          this.resetFromVillageDown();
        }
        return;
      }

      this.SCList =
        await SC.FetchEntireListByPHCRefs(
          this.phc_ids
        );

      this.sc_ids =
        this.sc_ids.filter(id =>
          this.SCList.some(
            sc => sc.p.id === id
          )
        );

      await this.autoSelectIfSingle(
        this.SCList,
        () => this.sc_ids,
        ids => this.sc_ids = ids,
        value => this.IsSCDisabled = value,
        async () => await this.fetchVillages()
      );

      if (
        this.sc_ids.length > 0 &&
        this.VillageList.length === 0
      ) {
        await this.fetchVillages();
      }
    } catch (err: any) {
      this.uiUtils.showErrorMessage(
        "Error",
        err?.message || String(err)
      );
    }
  }

  // ============================================================
  // VILLAGE
  // ============================================================

  private async fetchVillages(): Promise<void> {
    try {
      if (!this.sc_ids.length) {
        this.VillageList = [];
        if (!this.isRestoringFilter) {
          this.resetFromVillageDown();
        }
        return;
      }

      this.VillageList =
        await Village.FetchEntireListBySCRefs(
          this.sc_ids
        );

      this.village_ids =
        this.village_ids.filter(id =>
          this.VillageList.some(
            village => village.p.id === id
          )
        );
      await this.autoSelectIfSingle(
        this.VillageList,
        () => this.village_ids,
        ids => this.village_ids = ids,
        value => this.IsVillageDisabled = value,
        async () => await this.fetchAshas()
      );

      if (
        this.village_ids.length > 0 &&
        this.AshaList.length === 0
      ) {
        await this.fetchAshas();
      }

    } catch (err: any) {
      this.uiUtils.showErrorMessage(
        "Error",
        err?.message || String(err)
      );
    }
  }


  // ============================================================
  // ASHA
  // ============================================================

  private async fetchAshas(): Promise<void> {
    try {
      if (!this.village_ids.length) {
        this.AshaList = [];
        if (!this.isRestoringFilter) {
          this.asha_ids = [];
        }
        return;
      }

      this.AshaList =
        await AshaEmployeeRecord
          .FetchEntireListByVillageRefs(
            this.village_ids
          );

      this.asha_ids =
        this.asha_ids.filter(id =>
          this.AshaList.some(
            asha => asha.p.id === id
          )
        );

      await this.autoSelectIfSingle(
        this.AshaList,
        () => this.asha_ids,
        ids => this.asha_ids = ids,
        value => this.IsAshaDisabled = value
      );

    } catch (err: any) {
      this.uiUtils.showErrorMessage(
        "Error",
        err?.message || String(err)
      );
    }
  }


  // ============================================================
  // CHANGE HANDLERS
  // ============================================================

  async onStateChanged(e: any): Promise<void> {
    if (this.isInitializing) {
      return;
    }

    await this.withLoading(async () => {
      this.resetFromZoneDown();
      await this.fetchZones();
    });
  }

  async onZoneChanged(e: any): Promise<void> {
    if (this.isInitializing) {
      return;
    }

    await this.withLoading(async () => {
      this.resetFromDistrictDown();
      await this.fetchDistricts();
    });
  }

  async onDistrictChanged(e: any): Promise<void> {
    if (this.isInitializing) {
      return;
    }

    await this.withLoading(async () => {
      this.resetFromBlockDown();
      await this.fetchBlocks();
    });
  }

  async onBlockChanged(e: any): Promise<void> {
    if (this.isInitializing) {
      return;
    }

    await this.withLoading(async () => {
      this.resetFromBCMPostDown();
      await this.fetchBCMPosts();
    });
  }

  async onBCMPostChanged(e: any): Promise<void> {
    if (this.isInitializing) {
      return;
    }

    await this.withLoading(async () => {
      this.resetFromPHCDown();
      await this.fetchPHCs();
    });
  }

  async onPHCChanged(e: any): Promise<void> {
    if (this.isInitializing) {
      return;
    }

    await this.withLoading(async () => {
      this.resetFromAFDown();
      await this.fetchAFs();
    });
  }

  async onAFPostChanged(e: any): Promise<void> {
    if (this.isInitializing) {
      return;
    }

    await this.withLoading(async () => {
      this.resetFromSCDown();
      await this.fetchSCs();
    });
  }

  async onSCChanged(e: any): Promise<void> {
    if (this.isInitializing) {
      return;
    }

    await this.withLoading(async () => {
      this.resetFromVillageDown();
      await this.fetchVillages();
    });
  }

  async onVillageChanged(e: any): Promise<void> {
    if (this.isInitializing) {
      return;
    }

    await this.withLoading(async () => {
      this.asha_ids = [];
      this.AshaList = [];
      await this.fetchAshas();
    });
  }


  async onAshaChanged(e: any): Promise<void> {
    if (this.isInitializing) {
      return;
    }
  }


  // ============================================================
  // APPLY
  // ============================================================

  applyFilters(): void {

    if (this.storageKey) {
      const ids = {
        state_ids: [...this.state_ids],
        zone_ids: [...this.zone_ids],
        district_ids: [...this.district_ids],
        block_ids: [...this.block_ids],
        bcm_post_ids: [...this.bcm_post_ids],
        phc_ids: [...this.phc_ids],
        af_ids: [...this.af_ids],
        sc_ids: [...this.sc_ids],
        village_ids: [...this.village_ids],
        asha_ids: [...this.asha_ids]
      };

      if (this.storageKey === 'dashboard_filter_state') {
        this.filterStorage.setFilterState(ids);
      } else {
        try {
          sessionStorage.setItem(this.storageKey, JSON.stringify(ids));
        } catch (e) {
          console.error(`Failed to save filter state for key: ${this.storageKey}`, e);
        }
      }
    }

    this.captureSnapshot();

    this.filterState$.next({

      state_ids: [...this.state_ids],
      zone_ids: [...this.zone_ids],
      district_ids: [...this.district_ids],
      block_ids: [...this.block_ids],
      bcm_post_ids: [...this.bcm_post_ids],
      phc_ids: [...this.phc_ids],
      af_ids: [...this.af_ids],
      sc_ids: [...this.sc_ids],
      village_ids: [...this.village_ids],
      asha_ids: [...this.asha_ids]
    });
  }


  // ============================================================
  // CLEAR
  // ============================================================

  async clearFilters(): Promise<void> {

    await this.withLoading(async () => {

      if (!this.IsStateDisabled) {
        this.state_ids = [];
      }

      if (!this.IsZoneDisabled) {
        this.zone_ids = [];
        this.ZoneList = [];
      }

      if (!this.IsDistrictDisabled) {
        this.district_ids = [];
        this.DistrictList = [];
      }

      if (!this.IsBlockDisabled) {
        this.block_ids = [];
        this.BlockList = [];
      }

      if (!this.IsBCMPostDisabled) {
        this.bcm_post_ids = [];
        this.BCMPostList = [];
      }

      if (!this.IsPHCDisabled) {
        this.phc_ids = [];
        this.PHCList = [];
      }

      if (!this.IsAFPostDisabled) {
        this.af_ids = [];
        this.AFList = [];
      }

      if (!this.IsSCDisabled) {
        this.sc_ids = [];
        this.SCList = [];
      }

      if (!this.IsVillageDisabled) {
        this.village_ids = [];
        this.VillageList = [];
      }

      if (!this.IsAshaDisabled) {
        this.asha_ids = [];
        this.AshaList = [];
      }

      const role =
        this.getCurrentRole();

      if (
        role === Roles.SuperAdmin ||
        role === Roles.Developer
      ) {
        await this.fetchZones();
      }
      else if (
        role === Roles.DHO ||
        role === Roles.DCM
      ) {
        await this.fetchBlocks();
      }
      else if (
        role === Roles.THO ||
        role === Roles.BCM
      ) {
        await this.fetchBCMPosts();
      }
      else if (
        role === Roles.AF ||
        role === Roles.MO
      ) {
        await this.fetchAFs();
      }
    });

    /*
     * Clear is an intentional action.
     * Therefore save the cleared state.
     */
    this.applyFilters();
  }


  // ============================================================
  // RESET CHAIN
  // ============================================================

  private resetFromZoneDown(): void {
    this.zone_ids = [];
    this.ZoneList = [];
    this.IsZoneDisabled = false;
    this.resetFromDistrictDown();
  }

  private resetFromDistrictDown(): void {
    this.district_ids = [];
    this.DistrictList = [];
    this.IsDistrictDisabled = false;
    this.resetFromBlockDown();
  }

  private resetFromBlockDown(): void {
    this.block_ids = [];
    this.BlockList = [];
    this.IsBlockDisabled = false;
    this.resetFromBCMPostDown();
  }

  private resetFromBCMPostDown(): void {
    this.bcm_post_ids = [];
    this.BCMPostList = [];
    this.IsBCMPostDisabled = false;
    this.resetFromPHCDown();
  }

  private resetFromPHCDown(): void {
    this.phc_ids = [];
    this.PHCList = [];
    this.IsPHCDisabled = false;
    this.resetFromAFDown();
  }

  private resetFromAFDown(): void {
    this.af_ids = [];
    this.AFList = [];
    this.IsAFPostDisabled = false;
    this.resetFromSCDown();
  }

  private resetFromSCDown(): void {
    this.sc_ids = [];
    this.SCList = [];
    this.IsSCDisabled = false;
    this.resetFromVillageDown();
  }

  private resetFromVillageDown(): void {
    this.village_ids = [];
    this.VillageList = [];
    this.IsVillageDisabled = false;
    this.asha_ids = [];
    this.AshaList = [];
    this.IsAshaDisabled = false;
  }


  // ============================================================
  // RESTORE
  // ============================================================

  private restoreSavedFilterState(): void {

    if (!this.storageKey) {
      return;
    }

    let savedState: any = null;

    if (this.storageKey === 'dashboard_filter_state') {
      savedState = this.filterStorage.getFilterState();
    } else {
      try {
        const raw = sessionStorage.getItem(this.storageKey);
        if (raw) {
          savedState = JSON.parse(raw);
        }
      } catch (e) {
        console.error(`Failed to restore filter state for key: ${this.storageKey}`, e);
      }
    }

    if (!savedState) {
      return;
    }

    this.state_ids =
      [...(savedState?.state_ids ?? [])];

    this.zone_ids =
      [...(savedState?.zone_ids ?? [])];

    this.district_ids =
      [...(savedState?.district_ids ?? [])];

    this.block_ids =
      [...(savedState?.block_ids ?? [])];

    this.bcm_post_ids =
      [...(savedState?.bcm_post_ids ?? [])];

    this.phc_ids =
      [...(savedState?.phc_ids ?? [])];

    this.af_ids =
      [...(savedState?.af_ids ?? [])];

    this.sc_ids =
      [...(savedState?.sc_ids ?? [])];

    this.village_ids =
      [...(savedState?.village_ids ?? [])];

    this.asha_ids =
      [...(savedState?.asha_ids ?? [])];

  }


  // ============================================================
  // VISIBLE FILTER
  // ============================================================

  get HasAnyVisibleFilter(): boolean {
    return (
      this.IsStateVisible ||
      this.IsZoneVisible ||
      this.IsDistrictVisible ||
      this.IsBlockVisible ||
      this.IsBCMPostVisible ||
      this.IsPHCVisible ||
      this.IsAFPostVisible ||
      this.IsSCVisible ||
      this.IsVillageVisible ||
      this.IsAshaVisible
    );
  }
}